<script setup lang="ts">
import { computed, ref } from 'vue';
import { QInput, useQuasar, type QInputProps } from 'quasar';
import type { PrintableCourseStudent } from '@/types/FlightSchoolStudent';
import FsumDialog from '@/components/Utilities/FsumDialog.vue';
import StudentRepository from '@/repositories/StudentRepository';
import type { UserFaaExamScheduleRequest } from '@/types/Request/UserFaaExamScheduleRequest';
import { isDateValid } from '@/functions/isDateValid';
import DateInput from '@/components/DateInput.vue';

const props = defineProps<{
    printableStudent: PrintableCourseStudent,
}>();

const padDateString = (dateString: string) => {
    const [month, day, year] = dateString.split('/');
    return `${month.padStart(2, '0')}/${day.padStart(2, '0')}/${year}`;
};

const $q = useQuasar();
const ruleDateOrEmpty = (val:string) => (!val || isDateValid(val, 'MM/DD/YYYY') || 'Please enter a valid date in the format MM/DD/YYYY');
const originalDate = ref<null|string>(props.printableStudent.examDateString === '--' ? null : padDateString(props.printableStudent.examDateString));
const date = ref<null|string>(props.printableStudent.examDateString === '--' ? null : padDateString(props.printableStudent.examDateString));
const dateEditable = ref(false);
const examDateInput = ref<QInput|null>(null);

const saveExamDate = async () => {
    if (date.value === originalDate.value) {
        dateEditable.value = false;
        return;
    }

    const isValid = await examDateInput.value?.validate();
    if (!isValid) {
        return;
    }

    const message = `Change exam date from ${originalDate.value ?? 'unset'} to ${date.value ?? 'unset'}?`;

    $q.dialog({
        component: FsumDialog,
        componentProps: {
            title: 'Save Exam Date?',
            message,
            okLabel: 'Save',
        },
    }).onOk(async () => {
        const request: UserFaaExamScheduleRequest = {
            studentId: props.printableStudent.id,
            ratingUuid: props.printableStudent.ratingUuid,
            examDate: date.value,
        };

        await StudentRepository.saveExamSchedule(request)
            .then(() => {
                $q.notify({
                    type: 'positive',
                    message: 'Exam date saved successfully',
                });

                originalDate.value = date.value;
                dateEditable.value = false;
            }).catch((error: Error) => {
                $q.notify({
                    type: 'negative',
                    message: 'Failed to save exam date',
                });

                date.value = originalDate.value;
                dateEditable.value = false;

                throw error;
            });
    }).onCancel(() => {
        date.value = originalDate.value;
        dateEditable.value = false;
    });
};

const inputProps = computed((): Omit<QInputProps, 'modelValue'> => ({
    rules: [ruleDateOrEmpty],
    borderless: !dateEditable.value,
    readonly: !dateEditable.value,
    dense: true,
    lazyRules: true,
    clearable: true,
}));
</script>
<template>
    <div class="row profile-row q-mb-md q-pb-md q-gutter-sm">
        <div class="col-sm col-12 q-my-auto">
            <q-icon
                class="q-pr-sm q-pb-xs"
                name="sym_o_description"
            />
            <span class="label">Exam Date:</span>
        </div>
        <div class="col">
            <div class="row justify-between">
                <div class="col-sm col-12 q-my-auto">
                    <DateInput
                        ref="examDateInput"
                        :class="['exam-date-input', 'q-pa-none', 'q-mr-sm']"
                        :placeholder="dateEditable ? 'MM/DD/YYYY' : '--'"
                        :model-value="date"
                        :input-props="inputProps"
                        :is-icon-prepended="false"
                        @update:model-value="date = $event"
                    />
                </div>
                <div
                    class="q-my-auto"
                >
                    <q-btn
                        :label="dateEditable ? 'Save' : 'Edit'"
                        :ripple="false"
                        class="anchor-button text-body2"
                        color="fsum-blue"
                        no-caps
                        dense
                        flat
                        @click="dateEditable ? saveExamDate() : dateEditable = true"
                    />
                </div>
            </div>
        </div>
    </div>
</template>
<style scoped>
.profile-row {
    border-bottom: 1px solid var(--fsum-gray5);
}
span.label {
    font-weight: bold;
}
:deep(.q-field--error .q-field__bottom--animated) {
    transform: none;
    position: relative;
}
.exam-date-input {
    max-width: 200px;
    min-width: 125px;
}
</style>
